import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCookies } from 'react-cookie';

import Navbar from "../components/Navbar";
import CourseFilter from "../components/CourseFilter";
import CommonGoal, { CommonGoalType } from "../components/CommonGoal";
import IndividualGoal, { IndividualGoalType } from "../components/IndividualGoal";
import CommonProgressDialog from "../components/CommonProgressDialog";
import IndividualProgressDialog from "../components/IndividualProgressDialog";
import AddStudentDialog from "../components/AddStudentDialog";
import EditDueDateDialog from "../components/EditDueDateDialog";
import GoalSkeleton from "../components/GoalSkeleton";
import GoalDetail from "../components/GoalDetail";

import doubleRightArrow from "../images/double-right-arrow.svg";

import { useFetch } from "../hooks/useFetch";

import '../styles/Page.css'
import EditStudentInfoDialog from "../components/EditStudentInfoDialog";
import { StudentCardProps2 } from "../components/IndividualStudentCard";
import DeleteProgressesDialog from "../components/DeleteProgressesDialog";
import GoalNotFound from "../components/GoalNotFound";
import SearchNotFound from "../components/SearchNotFound";
import FilterGoalButton from "../components/FilterGoalButton";
import ExportGoalButton from "../components/ExportGoalButton";
import ImportExcelDialog from "../components/ImportExcelDialog";
import EditCommonGoalDueDateDialog from "../components/EditCommonGoalDueDateDialog";
import CreateNewSemesterDialog from "../components/CreateNewSemesterDialog";
import AddNewCommonGoalAndStudents from "../components/AddNewCommonGoalAndStudents";

export default function Curriculum() {

  const { commonProgressID, individualProgressID, courseID, semesterID } = useParams()

  const [cookies] = useCookies(['user'])

  let navigate = useNavigate();

  useEffect(() => {
    if (!cookies?.user?.token) {
      navigate(`/`)
    }
  }, [])

  const [semesterId, setSemesterId] = useState<number>(Number(semesterID || 0) || Number(localStorage.getItem(`semester_selected`)))
  const [courseId, setCourseId] = useState<number>(Number(courseID || 0) || Number(localStorage.getItem(`course_id_selected`)))

  const [goalType, setGoalType] = useState(0)

  const [openCreateSemester, setOpenCreateSemester] = useState(false)
  const [openAddNewCommonGoal, setOpenAddNewCommonGoal] = useState(false)
  const [openAddNewStudents, setOpenAddNewStudents] = useState(false)
  const [openCommonGoalDetail, setOpenCommonDetail] = useState(false)
  const [openIndividualGoalDetail, setOpenIndividualGoalDetail] = useState(false)
  const [openCommonDialog, setOpenCommonDialog] = useState(false)
  const [openIndividualDialog, setOpenIndividualDialog] = useState(false)
  const [openEditCommonGoalDueDate, setOpenEditCommonGoalDueDate] = useState(false)
  const [openAddStudentDialog, setOpenAddStudentDialog] = useState(false)
  const [openImportExcelDialog, setOpenImportExcelDialog] = useState(false)
  const [openEditInfo, setOpenEditInfo] = useState(false);
  const [openEditDueDate, setOpenEditDueDate] = useState(false);
  const [openDeleteProgresses, setOpenDeleteProgresses] = useState(false);

  const [sortKey, setSortKey] = useState("studentId")
  const [search, setSearch] = useState("")

  const [goalIdTarget, setGoalIdTarget] = useState(-1)
  const [studentTargetList, setStudentTargetList] = useState<StudentCardProps2[]>([])

  const { data: getCommonGoalData, error: getCommonGoalError, loading: getCommonGoalLoading } = useFetch({
    url: `/CommonGoals?semesterId=${semesterId}&curriculumId=${courseId}`,
    method: "GET",
    headers: {
      'Authorization': `Bearer ${cookies?.user?.token}`
    },
  }, semesterId, courseId)

  const { data: getIndividualGoalData, error: getIndividualGoalError, loading: getIndividualGoalLoading } = useFetch({
    url: `/IndividualGoals`,
    method: "GET",
    headers: {
      'Authorization': `Bearer ${cookies?.user?.token}`
    },
  })

  const { data: getStudentsData, error: getStudentsError, loading: getStudentsLoading } = useFetch({
    url: `/Students?curriculumId=${courseId}&semesterId=${semesterId}`,
    method: 'get',
    headers: {
      'Authorization': `Bearer ${cookies?.user?.token}`
    }
  }, courseId, semesterId)

  const [commonGoal, setCommonGoal] = useState([])
  const [commonGoalForSearch, setCommonGoalForSearch] = useState<CommonGoalType[]>([])
  const [individualGoal, setIndividualGoal] = useState([])
  const [individualGoalForFilter, setIndividualGoalForFilter] = useState<IndividualGoalType[]>([])
  const [individualGoalForSearch, setIndividualGoalForSearch] = useState<IndividualGoalType[]>([])
  const [courseStudentList, setCourseStudentList] = useState<StudentCardProps2[]>([])

  const [numberOfStudentInCommonGoal, setNumberOfStudentInCommonGoal] = useState<{ [key: number]: number }>({})
  const [numberOfStudentInIndividualGoal, setNumberOfStudentInIndividualGoal] = useState<{ [key: number]: number }>({})

  useEffect(() => {
    setCommonGoal(getCommonGoalData)
  }, [getCommonGoalData])

  useEffect(() => {
    setIndividualGoal(getIndividualGoalData)
    setIndividualGoalForFilter(getIndividualGoalData)
  }, [getIndividualGoalData])

  useEffect(() => {
    setCourseStudentList(getStudentsData)
  }, [getStudentsData])

  const handleClickCloseCommon = () => {
    setOpenCommonDialog(false)
    navigate(`/curriculum/`)
  }

  const handleGoalIdTarget = (goalID: number) => {
    setGoalIdTarget(goalID)
  }

  const handleStudentTargetList = (studentList: StudentCardProps2[]) => {
    setStudentTargetList(studentList)
  }

  useEffect(() => {
    setOpenCommonDialog(commonProgressID !== undefined)
    setOpenIndividualDialog(individualProgressID !== undefined)
  }, [])

  const handleClickCloseIndividual = () => {
    setOpenIndividualDialog(false)
    navigate(`/curriculum/`)
  }

  useEffect(() => {
    setOpenIndividualDialog(individualProgressID !== undefined)
  }, [individualProgressID])

  const clearTarget = () => {
    setGoalIdTarget(-1)
    setStudentTargetList([])
  }

  const handleClickCloseCreateSemester = () => {
    setOpenCreateSemester(false)
  }

  const handleClickCloseAddNewCommonGoal = () => {
    setOpenAddNewCommonGoal(false)
  }

  const handleClickCloseAddNewStudents = () => {
    setOpenAddNewStudents(false)
  }

  const handleClickCloseGoalDetail = () => {
    setOpenCommonDetail(false)
    setOpenIndividualGoalDetail(false)
    clearTarget()
  }

  const handleClickCloseEditCommonGoalDueDate = () => {
    setOpenEditCommonGoalDueDate(false)
    clearTarget()
  }

  const handleClickCloseAddStudent = () => {
    setOpenAddStudentDialog(false)
    clearTarget()
  }

  const handleClickCloseImportExcel = () => {
    setOpenImportExcelDialog(false)
    clearTarget()
  }

  const handleClickCloseEditDueDate = () => {
    setOpenEditDueDate(false)
    clearTarget()
  }

  const handleClickCloseDeleteProgresses = () => {
    setOpenDeleteProgresses(false)
    clearTarget()
  }

  const handleBackDropClick = (e: any) => {
    if (e.target.className === "ProgressDialog") {

    }
  }

  const handleSortBy = (event: any) => {
    setSortKey(event.target.value)
  }

  useMemo(() => {
    if (search !== "") {
      setCommonGoalForSearch(
        getCommonGoalData.filter((goal: CommonGoalType) => {
          return numberOfStudentInCommonGoal[goal.id] > 0
        })
      )
    } else {
      setCommonGoalForSearch([])
    }
  }, [numberOfStudentInCommonGoal])

  useMemo(() => {
    if (search !== "") {
      setIndividualGoalForSearch(
        getIndividualGoalData.filter((goal: IndividualGoalType) => {
          return numberOfStudentInIndividualGoal[goal.id] > 0
        })
      )
    } else {
      setIndividualGoalForSearch([])
    }
  }, [numberOfStudentInIndividualGoal])

  const scrollHorizontal = (e: any) => {
    const scroll = e.deltaY
    const scrollContainer = document.getElementById("scroll-container")

    if (scrollContainer && (e.target.className.includes("Page-goal-list") || e.target.className.includes("common") || e.target.className.includes("Goal-goal"))) {
      if (scroll > 0) {
        scrollContainer.scrollLeft += 294
      } else if (scroll < 0) {
        scrollContainer.scrollLeft -= 294
      }
    }
  }

  const searchScrollHorizontal = (e: any) => {
    const scroll = e.deltaY
    const scrollContainer = document.getElementById("search-scroll-container")

    if (scrollContainer && (e.target.className.includes("Page-goal-list") || e.target.className.includes("common") || e.target.className.includes("Goal-goal"))) {
      if (scroll > 0) {
        scrollContainer.scrollLeft += 294
      } else if (scroll < 0) {
        scrollContainer.scrollLeft -= 294
      }
    }
  }


  return (
    <div className="Page">
      {
        openCreateSemester &&
        <CreateNewSemesterDialog  handleClickClose={handleClickCloseCreateSemester}/>
      }
      {
        openAddNewCommonGoal &&
        <AddNewCommonGoalAndStudents semesterId={semesterId} courseId={courseId} handleClickClose={handleClickCloseAddNewCommonGoal} />
      }
      {
        openAddNewStudents &&
        <AddNewCommonGoalAndStudents semesterId={semesterId} courseId={courseId} handleClickClose={handleClickCloseAddNewStudents} isStudent/>
      }
      {
        openEditCommonGoalDueDate &&
        <EditCommonGoalDueDateDialog goalIdTarget={goalIdTarget} goalList={commonGoal} handleClickClose={handleClickCloseEditCommonGoalDueDate} />
      }
      {
        openDeleteProgresses &&
        <DeleteProgressesDialog goalId={goalIdTarget} goalList={individualGoal} studentList={studentTargetList} setStudentList={setStudentTargetList} handleClickClose={handleClickCloseDeleteProgresses} isScholarship={false} />
      }
      {
        openEditDueDate &&
        <EditDueDateDialog goalId={goalIdTarget} goalList={individualGoal} studentList={studentTargetList} setStudentList={setStudentTargetList} handleClickClose={handleClickCloseEditDueDate} isScholarship={false} />
      }
      {
        openEditInfo &&
        <EditStudentInfoDialog setOpenEditInfo={setOpenEditInfo} />
      }
      {
        openCommonGoalDetail &&
        <GoalDetail goalIdTarget={goalIdTarget} commonGoalList={commonGoal} handleClickClose={handleClickCloseGoalDetail} />
      }
      {
        openIndividualGoalDetail &&
        <GoalDetail goalIdTarget={goalIdTarget} individualGoalList={individualGoal} handleClickClose={handleClickCloseGoalDetail} />
      }
      {
        openCommonDialog &&
        <CommonProgressDialog
          id={Number(commonProgressID)}
          handleClickClose={handleClickCloseCommon}
          handleBackDropClick={handleBackDropClick}
          commonGoalList={commonGoal}
          openEditInfo={openEditInfo}
          setOpenEditInfo={setOpenEditInfo}
        />
      }
      {
        openIndividualDialog &&
        <IndividualProgressDialog
          id={Number(individualProgressID)}
          handleClickClose={handleClickCloseIndividual}
          handleBackDropClick={handleBackDropClick}
          individualGoalList={individualGoal}
          isScholarship={false}
          openEditInfo={openEditInfo}
          setOpenEditInfo={setOpenEditInfo}
        />
      }
      {
        openAddStudentDialog &&
        <AddStudentDialog
          goalIdTarget={goalIdTarget}
          goalList={individualGoal}
          courseId={courseId}
          semesterId={semesterId}
          handleClickClose={handleClickCloseAddStudent}
          handleBackDropClick={handleBackDropClick}
          isScholarship={false}
        />
      }
      {
        openImportExcelDialog &&
        <ImportExcelDialog
          goalIdTarget={goalIdTarget}
          goalList={individualGoal}
          courseId={courseId}
          semesterId={semesterId}
          handleClickClose={handleClickCloseImportExcel}
          isScholarship={false}
        />
      }
      <Navbar setGoalType={setGoalType} />
      <CourseFilter
        courseId={courseId}
        semesterId={semesterId}
        setSemesterId={setSemesterId}
        setCourseId={setCourseId}
        setSearch={setSearch}
        openCreateSemester={openCreateSemester}
        setOpenCreateSemester={setOpenCreateSemester}
        setOpenAddNewStudents={setOpenAddNewStudents}
      />

      <div className="Page-actions-bar">
        <div className="Page-goal-switch-container flex gap-dot5">
          <button
            className={(goalType === 0) ? "Page-goal-switch active-switch" : "Page-goal-switch"}
            onClick={() => setGoalType(0)}
          >
            Common Goal
          </button>
          <button
            className={(goalType === 1) ? "Page-goal-switch active-switch" : "Page-goal-switch"}
            onClick={() => setGoalType(1)}
          >
            Individual Goal
          </button>
        </div>

        <div className="flex-container gap-dot75">
          {goalType === 0 &&
            <ExportGoalButton
              courseId={courseId}
              semesterId={semesterId}
            />
          }
          {goalType === 1 &&
            <FilterGoalButton goalList={individualGoal} filterGoalList={individualGoalForFilter} setFilterGoalList={setIndividualGoalForFilter} />
          }
          <div className="flex gap-dot25">
            <label htmlFor="sortby">Sort by</label>
            <select name="sortby" id="sortby" onChange={handleSortBy} value={sortKey} className="Page-sortby-selector">
              {goalType === 1 && <option value="deadline">Due Date</option>}
              <option value="studentId">Student ID</option>
              <option value="name">Name</option>
            </select>
          </div>
        </div>
      </div>
      {
        ((getCommonGoalLoading && goalType === 0) || (getIndividualGoalLoading && goalType === 1)) ? <GoalSkeleton /> : (
          <>
            {goalType === 0 && commonGoal &&
              <div className="Page-goal-list common" onWheel={scrollHorizontal} id="scroll-container" style={{ display: search !== "" ? "none" : "flex" }}>
                {commonGoal.map((props: any, index: number) => (
                  <div className="common" key={props.id}>
                    <CommonGoal
                      key={props.id}
                      id={props.id}
                      no={props.no}
                      goal={props.goal}
                      description={props.description}
                      deadline={props.deadline}
                      openDialog={openCommonDialog}
                      openGoalDetail={openCommonGoalDetail}
                      setOpenGoalDetail={setOpenCommonDetail}
                      openEditDueDate={openEditCommonGoalDueDate}
                      setOpenEditDueDate={setOpenEditCommonGoalDueDate}
                      handleGoalIdTarget={handleGoalIdTarget}
                      setOpenDialog={setOpenCommonDialog}
                      sortKey={sortKey}
                      search={search}
                      courseId={courseId}
                      semesterId={semesterId}
                      setNumberOfStudentInCommonGoal={setNumberOfStudentInCommonGoal}
                    />
                    {index + 1 < commonGoal.length && <img src={doubleRightArrow} alt="" className="Page-goal-list-arrow" />}
                  </div>
                ))}
              </div>}
            {goalType === 0 && commonGoalForSearch.length !== 0 && search !== "" &&
              <div className="Page-goal-list" onWheel={searchScrollHorizontal} id="search-scroll-container">
                {commonGoalForSearch.map((props: any, index: number) => (
                  <CommonGoal
                    key={props.id}
                    id={props.id}
                    no={props.no}
                    goal={props.goal}
                    description={props.description}
                    deadline={props.deadline}
                    openDialog={openCommonDialog}
                    setOpenDialog={setOpenCommonDialog}
                    openGoalDetail={openCommonGoalDetail}
                    setOpenGoalDetail={setOpenCommonDetail}
                    openEditDueDate={openEditCommonGoalDueDate}
                    setOpenEditDueDate={setOpenEditCommonGoalDueDate}
                    handleGoalIdTarget={handleGoalIdTarget}
                    sortKey={sortKey}
                    search={search}
                    courseId={courseId}
                    semesterId={semesterId}
                  />
                ))}
              </div>}

            {goalType === 1 && individualGoal &&
              <div className="Page-goal-list" onWheel={scrollHorizontal} id="scroll-container" style={{ display: search !== "" ? "none" : "flex" }}>
                {
                  (individualGoalForFilter.length !== 0 && individualGoalForFilter.length < individualGoal.length) ? (
                    individualGoalForFilter.map((props: any) => (
                      <IndividualGoal
                        key={props.id}
                        id={props.id}
                        no={props.no}
                        goal={props.goal}
                        description={props.description}
                        semesterId={semesterId}
                        courseId={courseId}
                        openDialog={openIndividualDialog}
                        setOpenDialog={setOpenIndividualDialog}
                        openAddStudentDialog={openAddStudentDialog}
                        setOpenAddStudentDialog={setOpenAddStudentDialog}
                        openImportExcelDialog={openImportExcelDialog}
                        setOpenImportExcelDialog={setOpenImportExcelDialog}
                        openEditDueDateDialog={openEditDueDate}
                        setOpenEditDueDateDialog={setOpenEditDueDate}
                        openDeleteProgressesDialog={openDeleteProgresses}
                        setOpenDeleteProgressesDialog={setOpenDeleteProgresses}
                        openGoalDetail={openIndividualGoalDetail}
                        setOpenGoalDetail={setOpenIndividualGoalDetail}
                        handleGoalIdTarget={handleGoalIdTarget}
                        handleStudentTargetList={handleStudentTargetList}
                        sortKey={sortKey}
                        search={search}
                        beginPath={`/curriculum/${courseId}/${semesterId}/individual_goal`}
                        isScholarship={false}
                        courseStudentList={courseStudentList}
                        setNumberOfStudentInIndividualGoal={setNumberOfStudentInIndividualGoal}
                      />
                    ))
                  ) : (
                    individualGoal.map((props: any) => (
                      <IndividualGoal
                        key={props.id}
                        id={props.id}
                        no={props.no}
                        goal={props.goal}
                        description={props.description}
                        semesterId={semesterId}
                        courseId={courseId}
                        openDialog={openIndividualDialog}
                        setOpenDialog={setOpenIndividualDialog}
                        openAddStudentDialog={openAddStudentDialog}
                        setOpenAddStudentDialog={setOpenAddStudentDialog}
                        openImportExcelDialog={openImportExcelDialog}
                        setOpenImportExcelDialog={setOpenImportExcelDialog}
                        openEditDueDateDialog={openEditDueDate}
                        setOpenEditDueDateDialog={setOpenEditDueDate}
                        openDeleteProgressesDialog={openDeleteProgresses}
                        setOpenDeleteProgressesDialog={setOpenDeleteProgresses}
                        openGoalDetail={openIndividualGoalDetail}
                        setOpenGoalDetail={setOpenIndividualGoalDetail}
                        handleGoalIdTarget={handleGoalIdTarget}
                        handleStudentTargetList={handleStudentTargetList}
                        sortKey={sortKey}
                        search={search}
                        beginPath={`/curriculum/${courseId}/${semesterId}/individual_goal`}
                        isScholarship={false}
                        courseStudentList={courseStudentList}
                        setNumberOfStudentInIndividualGoal={setNumberOfStudentInIndividualGoal}
                      />
                    ))
                  )
                }
              </div>}
            {goalType === 1 && individualGoalForSearch.length !== 0 && search !== "" &&
              <div className="Page-goal-list" onWheel={searchScrollHorizontal} id="search-scroll-container">
                {individualGoalForSearch.map((props: any) => (
                  <IndividualGoal
                    key={props.id}
                    id={props.id}
                    no={props.no}
                    goal={props.goal}
                    description={props.description}
                    semesterId={semesterId}
                    courseId={courseId}
                    openDialog={openIndividualDialog}
                    setOpenDialog={setOpenIndividualDialog}
                    openAddStudentDialog={openAddStudentDialog}
                    setOpenAddStudentDialog={setOpenAddStudentDialog}
                    openImportExcelDialog={openImportExcelDialog}
                    setOpenImportExcelDialog={setOpenImportExcelDialog}
                    openEditDueDateDialog={openEditDueDate}
                    setOpenEditDueDateDialog={setOpenEditDueDate}
                    openDeleteProgressesDialog={openDeleteProgresses}
                    setOpenDeleteProgressesDialog={setOpenDeleteProgresses}
                    openGoalDetail={openIndividualGoalDetail}
                    setOpenGoalDetail={setOpenIndividualGoalDetail}
                    handleGoalIdTarget={handleGoalIdTarget}
                    handleStudentTargetList={handleStudentTargetList}
                    sortKey={sortKey}
                    search={search}
                    beginPath={`/curriculum/${courseId}/${semesterId}/individual_goal`}
                    isScholarship={false}
                    courseStudentList={courseStudentList}
                  />
                ))}
              </div>
            }
            {search === "" && ((goalType === 0 && commonGoal !== null && commonGoal.length === 0) || (goalType === 1 && individualGoal !== null && individualGoal.length === 0)) &&
              <div className="Page-goal-list">
                <GoalNotFound setOpenAddNewCommonGoal={setOpenAddNewCommonGoal}/>
              </div>
            }
            {search !== "" && ((goalType === 0 && commonGoalForSearch.length === 0) || (goalType === 1 && individualGoalForSearch.length === 0)) &&
              <div className="Page-goal-list">
                <SearchNotFound />
              </div>
            }
          </>
        )
      }
    </div>

  );
}